// Pre-built industry form templates for RoofGenius

export interface FormField {
  id: string;
  type: 'text' | 'number' | 'date' | 'checkbox' | 'radio' | 'select' | 'photo' | 'signature' | 'location' | 'textarea';
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[]; // For radio, select, checkbox
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
  conditionalLogic?: {
    showIf: {
      fieldId: string;
      operator: 'equals' | 'notEquals' | 'contains';
      value: any;
    };
  };
}

export interface SystemFormTemplate {
  id: string;
  name: string;
  description: string;
  category: 'inspection' | 'compliance' | 'customer' | 'production' | 'insurance';
  fields: FormField[];
  isSystemTemplate: true;
}

export const SYSTEM_FORM_TEMPLATES: SystemFormTemplate[] = [
  // INSPECTION FORMS
  {
    id: 'pre-work-inspection',
    name: 'Pre-Work Roof Inspection',
    description: 'Complete checklist before starting roof work',
    category: 'inspection',
    isSystemTemplate: true,
    fields: [
      { id: 'inspector-name', type: 'text', label: 'Inspector Name', required: true },
      { id: 'inspection-date', type: 'date', label: 'Inspection Date', required: true },
      { id: 'property-address', type: 'text', label: 'Property Address', required: true },
      { id: 'weather-conditions', type: 'select', label: 'Weather Conditions', required: true, options: ['Clear', 'Cloudy', 'Light Rain', 'Heavy Rain', 'Snow', 'Windy'] },
      { id: 'roof-access', type: 'radio', label: 'Roof Access Safe?', required: true, options: ['Yes', 'No', 'Needs Improvement'] },
      { id: 'existing-damage', type: 'textarea', label: 'Existing Damage Notes', required: false },
      { id: 'safety-hazards', type: 'checkbox', label: 'Safety Hazards Present', required: true, options: ['Power Lines', 'Unstable Structure', 'Sharp Objects', 'Wildlife', 'None'] },
      { id: 'photos-before', type: 'photo', label: 'Before Photos', required: true },
      { id: 'inspector-signature', type: 'signature', label: 'Inspector Signature', required: true },
    ],
  },
  {
    id: 'post-work-quality-check',
    name: 'Post-Work Quality Checklist',
    description: 'Quality assurance after job completion',
    category: 'inspection',
    isSystemTemplate: true,
    fields: [
      { id: 'inspector-name', type: 'text', label: 'Inspector Name', required: true },
      { id: 'completion-date', type: 'date', label: 'Completion Date', required: true },
      { id: 'workmanship-quality', type: 'radio', label: 'Workmanship Quality', required: true, options: ['Excellent', 'Good', 'Fair', 'Needs Correction'] },
      { id: 'materials-installed', type: 'checkbox', label: 'Materials Installed Correctly', required: true, options: ['Shingles', 'Flashing', 'Valleys', 'Ridge Cap', 'Gutters', 'Vents'] },
      { id: 'cleanup-complete', type: 'radio', label: 'Cleanup Complete?', required: true, options: ['Yes', 'No', 'Partial'] },
      { id: 'debris-removed', type: 'checkbox', label: 'Debris Removed', required: true, options: ['Yes'] },
      { id: 'photos-after', type: 'photo', label: 'After Photos', required: true },
      { id: 'issues-found', type: 'textarea', label: 'Issues or Corrections Needed', required: false },
      { id: 'customer-walkthrough', type: 'radio', label: 'Customer Walkthrough Completed?', required: true, options: ['Yes', 'No', 'Scheduled'] },
      { id: 'qc-signature', type: 'signature', label: 'QC Inspector Signature', required: true },
    ],
  },
  {
    id: 'roof-condition-assessment',
    name: 'Roof Condition Assessment',
    description: 'Detailed assessment of current roof condition',
    category: 'inspection',
    isSystemTemplate: true,
    fields: [
      { id: 'assessor-name', type: 'text', label: 'Assessor Name', required: true },
      { id: 'assessment-date', type: 'date', label: 'Assessment Date', required: true },
      { id: 'roof-age', type: 'number', label: 'Estimated Roof Age (years)', required: true },
      { id: 'roof-type', type: 'select', label: 'Roof Type', required: true, options: ['Asphalt Shingles', 'Metal', 'Tile', 'TPO', 'EPDM', 'Modified Bitumen', 'Other'] },
      { id: 'overall-condition', type: 'radio', label: 'Overall Condition', required: true, options: ['Excellent', 'Good', 'Fair', 'Poor', 'Critical'] },
      { id: 'damage-types', type: 'checkbox', label: 'Damage Types Present', required: true, options: ['Missing Shingles', 'Hail Damage', 'Wind Damage', 'Leaks', 'Ponding Water', 'Structural Issues', 'None'] },
      { id: 'life-expectancy', type: 'number', label: 'Remaining Life Expectancy (years)', required: false },
      { id: 'repair-recommendations', type: 'textarea', label: 'Repair Recommendations', required: true },
      { id: 'assessment-photos', type: 'photo', label: 'Assessment Photos', required: true },
    ],
  },

  // COMPLIANCE FORMS
  {
    id: 'three-day-right-to-cancel',
    name: '3-Day Right to Cancel',
    description: 'Required notice of cancellation rights',
    category: 'compliance',
    isSystemTemplate: true,
    fields: [
      { id: 'customer-name', type: 'text', label: 'Customer Name', required: true },
      { id: 'contract-date', type: 'date', label: 'Contract Date', required: true },
      { id: 'cancellation-deadline', type: 'date', label: 'Cancellation Deadline (3 days)', required: true },
      { id: 'customer-acknowledgment', type: 'checkbox', label: 'Customer Acknowledgment', required: true, options: ['I understand I have 3 business days to cancel this contract'] },
      { id: 'customer-signature', type: 'signature', label: 'Customer Signature', required: true },
      { id: 'contractor-signature', type: 'signature', label: 'Contractor Signature', required: true },
    ],
  },
  {
    id: 'lien-waiver',
    name: 'Lien Waiver',
    description: 'Release of mechanic\'s lien rights',
    category: 'compliance',
    isSystemTemplate: true,
    fields: [
      { id: 'contractor-name', type: 'text', label: 'Contractor Name', required: true },
      { id: 'property-owner', type: 'text', label: 'Property Owner', required: true },
      { id: 'property-address', type: 'text', label: 'Property Address', required: true },
      { id: 'work-description', type: 'textarea', label: 'Work Description', required: true },
      { id: 'payment-amount', type: 'number', label: 'Payment Amount ($)', required: true },
      { id: 'payment-date', type: 'date', label: 'Payment Date', required: true },
      { id: 'waiver-type', type: 'radio', label: 'Waiver Type', required: true, options: ['Conditional', 'Unconditional', 'Partial', 'Final'] },
      { id: 'contractor-signature', type: 'signature', label: 'Contractor Signature', required: true },
    ],
  },
  {
    id: 'permit-application',
    name: 'Permit Application Checklist',
    description: 'Documentation for building permit application',
    category: 'compliance',
    isSystemTemplate: true,
    fields: [
      { id: 'applicant-name', type: 'text', label: 'Applicant Name', required: true },
      { id: 'property-address', type: 'text', label: 'Property Address', required: true },
      { id: 'permit-type', type: 'select', label: 'Permit Type', required: true, options: ['Roof Replacement', 'Roof Repair', 'New Construction', 'Addition', 'Other'] },
      { id: 'documents-submitted', type: 'checkbox', label: 'Documents Submitted', required: true, options: ['Plans/Drawings', 'Engineering Specs', 'Material Specs', 'Insurance Certificate', 'Contractor License'] },
      { id: 'permit-number', type: 'text', label: 'Permit Number (if issued)', required: false },
      { id: 'submission-date', type: 'date', label: 'Submission Date', required: true },
      { id: 'notes', type: 'textarea', label: 'Additional Notes', required: false },
    ],
  },

  // CUSTOMER FORMS
  {
    id: 'customer-satisfaction-survey',
    name: 'Customer Satisfaction Survey',
    description: 'Post-job customer feedback collection',
    category: 'customer',
    isSystemTemplate: true,
    fields: [
      { id: 'customer-name', type: 'text', label: 'Customer Name', required: true },
      { id: 'completion-date', type: 'date', label: 'Job Completion Date', required: true },
      { id: 'overall-rating', type: 'radio', label: 'Overall Satisfaction', required: true, options: ['Very Satisfied', 'Satisfied', 'Neutral', 'Dissatisfied', 'Very Dissatisfied'] },
      { id: 'quality-rating', type: 'radio', label: 'Workmanship Quality', required: true, options: ['Excellent', 'Good', 'Fair', 'Poor'] },
      { id: 'communication-rating', type: 'radio', label: 'Communication', required: true, options: ['Excellent', 'Good', 'Fair', 'Poor'] },
      { id: 'timeliness-rating', type: 'radio', label: 'Timeliness', required: true, options: ['Excellent', 'Good', 'Fair', 'Poor'] },
      { id: 'cleanup-rating', type: 'radio', label: 'Cleanup', required: true, options: ['Excellent', 'Good', 'Fair', 'Poor'] },
      { id: 'would-recommend', type: 'radio', label: 'Would You Recommend Us?', required: true, options: ['Yes', 'Maybe', 'No'] },
      { id: 'feedback', type: 'textarea', label: 'Additional Feedback', required: false },
      { id: 'testimonial-permission', type: 'checkbox', label: 'Permission to Use as Testimonial', required: false, options: ['Yes, you may use my feedback'] },
    ],
  },

  // PRODUCTION FORMS
  {
    id: 'daily-progress-report',
    name: 'Daily Progress Report',
    description: 'Track daily job progress and activities',
    category: 'production',
    isSystemTemplate: true,
    fields: [
      { id: 'foreman-name', type: 'text', label: 'Foreman Name', required: true },
      { id: 'report-date', type: 'date', label: 'Report Date', required: true },
      { id: 'job-address', type: 'text', label: 'Job Address', required: true },
      { id: 'crew-members', type: 'number', label: 'Number of Crew Members', required: true },
      { id: 'start-time', type: 'text', label: 'Start Time', required: true },
      { id: 'end-time', type: 'text', label: 'End Time', required: true },
      { id: 'work-completed', type: 'textarea', label: 'Work Completed Today', required: true },
      { id: 'materials-used', type: 'textarea', label: 'Materials Used', required: true },
      { id: 'weather-conditions', type: 'select', label: 'Weather', required: true, options: ['Clear', 'Cloudy', 'Rain', 'Snow', 'Windy'] },
      { id: 'issues-delays', type: 'textarea', label: 'Issues or Delays', required: false },
      { id: 'percent-complete', type: 'number', label: 'Percent Complete (%)', required: true, validation: { min: 0, max: 100 } },
      { id: 'progress-photos', type: 'photo', label: 'Progress Photos', required: false },
      { id: 'foreman-signature', type: 'signature', label: 'Foreman Signature', required: true },
    ],
  },
  {
    id: 'safety-checklist',
    name: 'Safety Checklist',
    description: 'Daily safety inspection and compliance',
    category: 'production',
    isSystemTemplate: true,
    fields: [
      { id: 'safety-officer', type: 'text', label: 'Safety Officer', required: true },
      { id: 'inspection-date', type: 'date', label: 'Inspection Date', required: true },
      { id: 'ppe-inspection', type: 'checkbox', label: 'PPE Equipment Present', required: true, options: ['Hard Hats', 'Safety Harnesses', 'Safety Glasses', 'Gloves', 'Steel-Toe Boots'] },
      { id: 'fall-protection', type: 'radio', label: 'Fall Protection Adequate?', required: true, options: ['Yes', 'No', 'N/A'] },
      { id: 'ladder-condition', type: 'radio', label: 'Ladder Condition', required: true, options: ['Good', 'Fair', 'Unsafe'] },
      { id: 'first-aid-kit', type: 'radio', label: 'First Aid Kit Available?', required: true, options: ['Yes', 'No'] },
      { id: 'hazards-identified', type: 'textarea', label: 'Hazards Identified', required: false },
      { id: 'corrective-actions', type: 'textarea', label: 'Corrective Actions Taken', required: false },
      { id: 'safety-briefing', type: 'radio', label: 'Safety Briefing Conducted?', required: true, options: ['Yes', 'No'] },
      { id: 'officer-signature', type: 'signature', label: 'Safety Officer Signature', required: true },
    ],
  },

  // INSURANCE FORMS
  {
    id: 'damage-documentation',
    name: 'Damage Documentation Form',
    description: 'Detailed damage documentation for insurance claims',
    category: 'insurance',
    isSystemTemplate: true,
    fields: [
      { id: 'inspector-name', type: 'text', label: 'Inspector Name', required: true },
      { id: 'inspection-date', type: 'date', label: 'Inspection Date', required: true },
      { id: 'claim-number', type: 'text', label: 'Insurance Claim Number', required: false },
      { id: 'damage-cause', type: 'select', label: 'Cause of Damage', required: true, options: ['Hail', 'Wind', 'Fire', 'Water', 'Storm', 'Age/Wear', 'Unknown'] },
      { id: 'damage-date', type: 'date', label: 'Date of Damage', required: true },
      { id: 'damage-areas', type: 'checkbox', label: 'Damaged Areas', required: true, options: ['Roof Deck', 'Shingles', 'Flashing', 'Gutters', 'Siding', 'Windows', 'Interior'] },
      { id: 'damage-severity', type: 'radio', label: 'Damage Severity', required: true, options: ['Minor', 'Moderate', 'Severe', 'Total Loss'] },
      { id: 'square-footage', type: 'number', label: 'Damaged Square Footage', required: false },
      { id: 'detailed-description', type: 'textarea', label: 'Detailed Damage Description', required: true },
      { id: 'damage-photos', type: 'photo', label: 'Damage Photos (Multiple)', required: true },
      { id: 'emergency-repairs', type: 'textarea', label: 'Emergency Repairs Needed', required: false },
      { id: 'inspector-signature', type: 'signature', label: 'Inspector Signature', required: true },
    ],
  },
  {
    id: 'adjuster-meeting-notes',
    name: 'Adjuster Meeting Notes',
    description: 'Documentation from insurance adjuster meeting',
    category: 'insurance',
    isSystemTemplate: true,
    fields: [
      { id: 'meeting-date', type: 'date', label: 'Meeting Date', required: true },
      { id: 'adjuster-name', type: 'text', label: 'Adjuster Name', required: true },
      { id: 'adjuster-company', type: 'text', label: 'Insurance Company', required: true },
      { id: 'claim-number', type: 'text', label: 'Claim Number', required: true },
      { id: 'items-discussed', type: 'textarea', label: 'Items Discussed', required: true },
      { id: 'agreed-scope', type: 'textarea', label: 'Agreed Scope of Work', required: true },
      { id: 'disputed-items', type: 'textarea', label: 'Disputed Items', required: false },
      { id: 'next-steps', type: 'textarea', label: 'Next Steps', required: true },
      { id: 'follow-up-date', type: 'date', label: 'Follow-Up Date', required: false },
      { id: 'meeting-photos', type: 'photo', label: 'Meeting Photos', required: false },
      { id: 'our-rep-signature', type: 'signature', label: 'Our Representative Signature', required: true },
    ],
  },

  // FULL PROPERTY INSPECTION CHECKLIST (modeled after RB Restoration Builders 2-page form)
  {
    id: 'property-inspection-checklist-full',
    name: 'Property Inspection Checklist (Full)',
    description: 'Complete 2-page exterior + interior inspection: roofing, attic, siding, gutters, HVAC, drywall, painting, and homeowner opportunity summary',
    category: 'inspection',
    isSystemTemplate: true,
    fields: [
      // ---- Client Information ----
      { id: 'inspection-date', type: 'date', label: 'Inspection Date', required: true },
      { id: 'client-name', type: 'text', label: 'Client Name', required: true },
      { id: 'property-address', type: 'text', label: 'Property Address', required: true },
      { id: 'client-phone', type: 'text', label: 'Phone', required: false },
      { id: 'client-email', type: 'text', label: 'Email', required: false },
      { id: 'project-number', type: 'text', label: 'Project Number', required: false },

      // ---- Roofing ----
      { id: 'roof-shingles', type: 'radio', label: 'Roofing — Shingles', required: true, options: ['Damaged', 'Missing', 'Fair', 'Good'] },
      { id: 'roof-flashing', type: 'radio', label: 'Roofing — Flashing', required: true, options: ['Damaged', 'Missing', 'Fair', 'Good'] },
      { id: 'roof-valleys', type: 'radio', label: 'Roofing — Valleys', required: true, options: ['Damaged', 'Missing', 'Fair', 'Good'] },
      { id: 'roof-ridge-vents', type: 'radio', label: 'Roofing — Ridge Vents', required: true, options: ['Damaged', 'Missing', 'Fair', 'Good'] },
      { id: 'roof-eave-vents', type: 'radio', label: 'Roofing — Eave Vents', required: true, options: ['Damaged', 'Missing', 'Fair', 'Good'] },
      { id: 'roof-notes', type: 'textarea', label: 'Roofing Notes', required: false },
      { id: 'roof-photos', type: 'photo', label: 'Roof Photos', required: false },

      // ---- Attic & Plywood ----
      { id: 'attic-conditions', type: 'checkbox', label: 'Attic Conditions', required: false, options: ['Insulation', 'Ventilation', 'Damaged', 'Mold'] },
      { id: 'attic-area-sqft', type: 'number', label: 'Damaged Area Measure (sq ft)', required: false },
      { id: 'attic-sheet-area', type: 'number', label: 'Sheet Area (e.g. 32 for 8x4 sheet)', required: false },
      { id: 'attic-damaged-sheets', type: 'number', label: 'Total Damaged Sheets to Replace', required: false },
      { id: 'attic-photos', type: 'photo', label: 'Attic / Plywood Photos', required: false },

      // ---- Siding & Windows ----
      { id: 'siding-condition', type: 'radio', label: 'Siding Condition', required: false, options: ['Damaged', 'Fair', 'Good'] },
      { id: 'siding-material', type: 'select', label: 'Siding Material Type', required: false, options: ['Vinyl', 'Hardie/Fiber Cement', 'Wood', 'Aluminum', 'Stucco', 'Brick', 'Other'] },
      { id: 'windows-condition', type: 'radio', label: 'Windows Condition', required: false, options: ['Damaged', 'Fair', 'Good'] },
      { id: 'windows-material', type: 'select', label: 'Window Material Type', required: false, options: ['Vinyl', 'Wood', 'Aluminum', 'Composite', 'Other'] },
      { id: 'siding-windows-notes', type: 'textarea', label: 'Siding & Windows Notes', required: false },

      // ---- Fences ----
      { id: 'fence-status', type: 'checkbox', label: 'Fences', required: false, options: ['Insulation', 'Ventilation', 'Condition', 'Mold', 'Good'] },
      { id: 'fence-damaged-sheets', type: 'number', label: 'Number of Damaged Sheets/Pickets', required: false },

      // ---- Drywall ----
      { id: 'drywall-conditions', type: 'checkbox', label: 'Drywall Conditions', required: false, options: ['Cracks', 'Water damage', 'Stains', 'Restore to Condition'] },
      { id: 'drywall-notes', type: 'textarea', label: 'Drywall Notes', required: false },

      // ---- Interior Painting ----
      { id: 'painting-rooms', type: 'textarea', label: 'Interior Painting — Rooms / Ceiling / Walls / Trim (list)', required: false, placeholder: 'e.g. Kitchen — ceiling + walls; Living Room — trim only' },

      // ---- Gutters / Fascia / Soffit ----
      { id: 'gutters-condition', type: 'radio', label: 'Gutters / Fascia / Soffit', required: false, options: ['Damaged', 'Fair', 'Good'] },
      { id: 'gutters-notes', type: 'textarea', label: 'Gutter Notes', required: false },

      // ---- HVAC ----
      { id: 'hvac-visual-age', type: 'text', label: 'HVAC — Visual Age', required: false },
      { id: 'hvac-condition', type: 'radio', label: 'HVAC Condition', required: false, options: ['Good', 'Fair', 'Poor', 'Replace'] },

      // ---- Water Purification ----
      { id: 'water-filter-present', type: 'radio', label: 'Water Purification System Present?', required: false, options: ['Yes', 'No'] },
      { id: 'water-filter-condition', type: 'radio', label: 'Water Filter Condition', required: false, options: ['Good', 'Needs Service', 'Replace'] },

      // ---- Homeowner Opportunity Summary ----
      { id: 'opportunity-summary', type: 'checkbox', label: 'Homeowner Opportunity Summary (recommended scope)', required: false, options: ['Roofing', 'Gutters', 'Siding', 'Windows', 'Painting', 'HVAC', 'Other'] },

      // ---- Preliminary Estimate ----
      { id: 'estimate-line-items', type: 'textarea', label: 'Preliminary Estimate (Item / Qty / Unit Cost / Total)', required: false, placeholder: 'e.g. Shingle Repair — 5 SQ — $450 — $2,250' },
      { id: 'estimate-total', type: 'number', label: 'Estimated Total ($)', required: false },

      // ---- Acknowledgment ----
      { id: 'acknowledgment', type: 'checkbox', label: 'Client Acknowledgment', required: true, options: ['I understand this is a preliminary visual assessment and not an engineering report. Estimate is preliminary and subject to a full contract.'] },
      { id: 'client-signature', type: 'signature', label: 'Client Signature', required: true },
      { id: 'rep-signature', type: 'signature', label: 'RB Rep / Sales Rep Signature', required: true },
      { id: 'final-photos', type: 'photo', label: 'Additional Site Photos', required: false },
    ],
  },
];
